/*
====================================
AVANCE DE ANAMNESIS ESTRUCTURADA
====================================

Cada elemento de una lista representa
UNA pregunta, aunque en el formulario
tenga más de un control.

Ejemplo:
"aprend_esfin_anio+aprend_esfin_mes"

Es una pregunta sobre la edad.
Se considera respondida si se completó
al menos uno de sus dos controles.

Los comentarios que acompañan a un
selector no se cuentan por separado.

Los campos de respuesta libre que
constituyen una pregunta propia sí
se incluyen en el avance.
====================================
*/


/*
====================================
COMPROBAR SI HAY RESPUESTA
====================================

No consideramos respondidos:
null, undefined, "" ni textos compuestos
únicamente por espacios.

El valor numérico 0 sí es una respuesta.
====================================
*/

const tieneRespuesta = (valor) => {

    if (valor === null || valor === undefined) {
        return false;
    }

    if (typeof valor === 'string') {
        return valor.trim() !== '';
    }

    return true;

};


/*
====================================
CREAR PREGUNTAS DESDE UNA LISTA
====================================

Cada nombre separado por espacios es
una pregunta diferente.

Los nombres unidos por "+" pertenecen
a la misma pregunta.
====================================
*/

const preguntas = (lista) => {

    return lista
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((nombre) => ({

            campos: nombre.split('+')

        }));

};


/*
====================================
PREGUNTAS CONDICIONALES
====================================

Solo forman parte del total cuando
se cumple la condición indicada.
====================================
*/

const preguntasCuando = (condicion, lista) => {

    return preguntas(lista).map((pregunta) => ({

        ...pregunta,

        condicion

    }));

};


/*
====================================
PREGUNTAS DE CADA SECCIÓN
====================================

Los nombres corresponden a las claves
del payload actual de Anamnesis.js.
====================================
*/

const secciones = [

    {
        id: 'General',

        preguntas: [

            ...preguntas(`
                medico_deriva
                quien_vive
                vivienda
                cantidad_habitaciones
                primera_consulta
                tratamientos_previos
                espaniol_materno
                bilingue
            `),

            ...preguntasCuando(
                (datos) => datos.primera_consulta === 'No',
                'fecha_primera_consulta'
            ),

            ...preguntasCuando(
                (datos) => datos.espaniol_materno === 'No',
                'idioma_materno'
            ),

            ...preguntasCuando(
                (datos) => datos.bilingue === 'Si',
                'idioma_bilingue'
            ),

            /*
            ====================================
            TRATAMIENTOS PREVIOS
            ====================================

            Si se responde "Si", se incorpora
            únicamente la pregunta Profesional 1.

            Cuando se selecciona su especialidad,
            se incorporan sus preguntas internas.

            Los profesionales 2 a 7 no suman
            preguntas al total hasta que se
            seleccione su especialidad.
            ====================================
            */

            ...Array.from(
                { length: 7 },
                (_, indice) => {

                    const numero = indice + 1;

                    const prefijo =
                        `tratamientos_previos_${numero}_`;

                    const hayTratamientos =
                        (datos) =>
                            datos.tratamientos_previos === 'Si';

                    const profesionalSeleccionado =
                        (datos) =>
                            hayTratamientos(datos) &&
                            tieneRespuesta(
                                datos[`${prefijo}prof`]
                            );

                    return [

                        /*
                        PROFESIONAL 1:
                        Se cuenta desde que se responde
                        "Si" en tratamientos previos.

                        PROFESIONALES 2 A 7:
                        Se cuentan únicamente cuando
                        se selecciona su especialidad.
                        */

                        ...preguntasCuando(
                            numero === 1
                                ? hayTratamientos
                                : profesionalSeleccionado,

                            `${prefijo}prof`
                        ),

                        /*
                        PREGUNTAS INTERNAS:
                        Solo se incorporan cuando
                        se seleccionó la especialidad
                        de ese profesional.
                        */

                        ...preguntasCuando(
                            profesionalSeleccionado,

                            `
                                ${prefijo}edad
                                ${prefijo}duracion_anios+${prefijo}duracion_meses
                                ${prefijo}institucion
                                ${prefijo}datos_profesional
                            `
                        )

                    ];

                }
            ).flat()

        ]

    },

    {
        id: 'Embarazo',

        preguntas: [

            ...preguntas(`
                estado_pareja
                embarazo_buscado
                sexo_esperado_madre
                sexo_esperado_padre
                sintomas
                enferm_acc_embarazo
                abortos_antes_despues
                asfixia_perinatal
            `),

            ...preguntasCuando(
                (datos) => datos.abortos_antes_despues === 'Si',
                `
                    cant_abortos
                    motivo_abortos
                    provocado_espontaneo
                `
            )

        ]

    },

    {
        id: 'Parto',

        preguntas: [

            ...preguntas(`
                parto
                parto_semana
                parto_duracion
                apgar
                peso
                parto_complicacion
                bebe_reaccion
                incubadora
            `),

            ...preguntasCuando(
                (datos) => datos.bebe_reaccion === 'Complicaciones',
                'bebe_complicacion'
            ),

            ...preguntasCuando(
                (datos) => datos.incubadora === 'Si',
                'incubadora_hs'
            )

        ]

    },

    {
        id: 'Lactancia',

        preguntas: [

            ...preguntas(`
                pecho
                bebe_lloron_comentario
                chupete
                succion_pulgar
                uso_fular
            `),

            ...preguntasCuando(
                (datos) => datos.pecho === 'Si',
                `
                    pecho_mes
                    pecho_prendio_comentario
                `
            ),

            ...preguntasCuando(
                (datos) => datos.chupete === 'Si',
                'chupete_mes'
            ),

            ...preguntasCuando(
                (datos) => datos.succion_pulgar === 'Si',
                'succion_pulgar_mes'
            ),

            ...preguntasCuando(
                (datos) => datos.uso_fular === 'Si',
                'uso_fular_hs'
            )

        ]

    },

    {
        id: 'Alimentacion',

        preguntas: [

            ...preguntas(`
                mamadera
                mamadera_solo_comentario
                adaptacion_solido_comentario
                dulce_salado_comentario
                aceptar_alimentacion_comentario
                rechazo_alimento_comentario
                comer_solo_comentario
                horarios_comida_comentario
            `),

            ...preguntasCuando(
                (datos) => datos.mamadera === 'Si',
                'mamadera_edad'
            )

        ]

    },

    {
        id: 'Denticion',

        preguntas: preguntas(`
            primer_diente_comentario
            diente_molestias_comentario
            segunda_denticion_comentario
            caida_dientes_comentario
        `)

    },

    {
        id: 'Motricidad',

        preguntas: preguntas(`
            cabeza_mes
            sento_mes
            gateo_mes
            camino_mes
            mano
            pie
            habil
        `)

    },

    {
        id: 'ControlEsfinteres',

        preguntas: [

            ...preguntas(`
                aprend_esfin_anio+aprend_esfin_mes
                reti_panial_dia_anio+reti_panial_dia_mes
                reti_panial_noche_anio+reti_panial_noche_mes
                ctr_esfin_dia_anio+ctr_esfin_dia_mes
                ctr_esfin_noche_anio+ctr_esfin_noche_mes
                ctr_esfin_reacc_padr_coment
                esfin_solo
                bania_solo_comentario
                viste_solo_comentario
                enuresis
                encopresis
            `),

            ...preguntasCuando(
                (datos) => datos.esfin_solo === 'Si',
                'esfin_solo_anio'
            )

        ]

    },

    {
        id: 'Lenguaje',

        preguntas: preguntas(`
            prim_palab_mes
            leng_dific_coment
            leng_coment
            leng_entend_coment
        `)

    },

    {
        id: 'Sueño',

        preguntas: [

            ...preguntas(`
                dond_dorm_naci_coment
                hizo_colecho
                hmnos_colecho
                nuevo_miemb_comentario
                donde_duerme_comentario
                duerme_solo_anio+duerme_solo_mes
                duerme_hs
                duerme_act_comentario
                duerme_luz
                duerme_juguete
                acostar_tranq_coment
                demora_dorm_coment
            `),

            ...preguntasCuando(
                (datos) => datos.duerme_juguete === 'Si',
                'duerme_juguete_cant'
            )

        ]

    },

    {
        id: 'ConductaSexual',

        preguntas: preguntas(`
            preg_sexual_coment
            info_sexual_coment
            tocar_genit_coment
            tocar_genit_act_coment
        `)

    },

    {
        id: 'Enfermedades',

        preguntas: preguntas(`
            enferm_coment
            internado_coment
            operado_coment
            accidente_coment
            perd_conocim_coment
            conv_mar_des_coment
            est_neurolog_coment
            est_neurolog_diag_coment
            tics_coment
            unias_coment
        `)

    },

    {
        id: 'Escolaridad',

        preguntas: [

            ...preguntas(`
                guarderia
                jardin
                jardin_iniciacion_coment
                jardin_adapt_coment
                repitio_anio
                docente_coment
                trastaornos_coment
                maestra_particular
            `),

            ...preguntasCuando(
                (datos) => datos.guarderia === 'Si',
                'guarderia_edad'
            ),

            ...preguntasCuando(
                (datos) => datos.jardin === 'Si',
                'jardin_edad'
            )

        ]

    },

    {
        id: 'VidaSocial',

        preguntas: preguntas(`
            amigos_coment
            act_preferidas_coment
            mudanza_coment
            festeja_cumple_coment
            vinculo_plaza_coment
        `)

    },

    {
        id: 'RelacionesFamiliares',

        preguntas: preguntas(`
            relac_padre_coment
            relac_madre_coment
            relac_hmno_coment
            relac_hmna_coment
            relac_pareja_coment
        `)

    },

    {
        id: 'Sensopercepcion',

        preguntas: preguntas(`
            etiquetas
            textura
            sonido
            frio_calor
        `)

    },

    {
        id: 'TerapiasActualesPrevias',

        preguntas: [

            ...preguntas(`
                terapia_madre
                terapia_padre
                orientacion_padres
                terapia_flia
                sustancias_toxicas
            `),

            ...preguntasCuando(
                (datos) => datos.orientacion_padres === 'Si',
                'orientacion_padres_cuando'
            ),

            ...preguntasCuando(
                (datos) => datos.terapia_flia === 'Si',
                'terapia_flia_cuando'
            ),

            ...preguntasCuando(
                (datos) => datos.sustancias_toxicas === 'Si',
                'sustancias_toxicas_cuando'
            )

        ]

    }

];


/*
====================================
CALCULAR AVANCE
====================================

Devuelve el avance de cada sección
y el avance general de la estructurada.

El total puede variar cuando se habilitan
preguntas condicionales.
====================================
*/

export const calcularAvanceAnamnesis = (datos) => {

    const resultadoSecciones = {};

    let totalPreguntas = 0;

    let totalRespondidas = 0;


    secciones.forEach((seccion) => {

        const preguntasAplicables =
            seccion.preguntas.filter((pregunta) => {

                return (
                    !pregunta.condicion ||
                    pregunta.condicion(datos)
                );

            });


        const respondidas =
            preguntasAplicables.filter((pregunta) => {

                return pregunta.campos.some((campo) => {

                    return tieneRespuesta(
                        datos[campo]
                    );

                });

            }).length;


        const total =
            preguntasAplicables.length;


        resultadoSecciones[seccion.id] = {

            respondidas,

            total,

            /*
            Una sección con preguntas pendientes
            NO se considera completa.
            */

            completa:
                total > 0 &&
                respondidas === total

        };


        totalPreguntas += total;

        totalRespondidas += respondidas;

    });


    return {

        secciones: resultadoSecciones,

        respondidas: totalRespondidas,

        total: totalPreguntas,

        porcentaje:
            totalPreguntas > 0
                ? Math.round(
                    totalRespondidas /
                    totalPreguntas * 100
                )
                : 0

    };

};